import type { Currency } from '@packages/domain/models/currency';
import type { Price } from '@packages/domain/models/price';
import { normalizeResponse } from '@packages/domain/utils/normalize/normalize';
import { PAYEX_URL } from 'constants/config';
import { schemas } from 'constants/schemas';
import type { Field } from 'models/field';
import type {
  GetOffersResponse,
  GetRecurringOffersResponse,
  OfferWithIds,
  SingleOfferResponse,
} from 'models/offer';
import type { Section } from 'models/section';
import { fetchWithAuthentication } from 'utils/fetchJson/fetchJson';

import type {
  GetAllOffersPayload,
  GetRecurringOffersPayload,
  GetSingleOffersPayload,
  NonFxDataResponse,
} from './model';

export type OffersService = {
  getAll: (payload: GetAllOffersPayload) => Promise<GetOffersResponse>;
  getSingleOffers: (
    payload: GetSingleOffersPayload,
  ) => Promise<SingleOfferResponse[]>;
  getRecurringOffers: (
    payload: GetRecurringOffersPayload,
  ) => Promise<GetRecurringOffersResponse>;
  getNonFxOptions: () => Promise<NonFxDataResponse>;
};

export const offers: OffersService = {
  getAll: ({
    amount,
    country,
    recipientId,
    fingerprint,
    locale,
    recurringConfigurationId,
    recipientFields,
    sourceOfFunds,
    financialInstitution,
  }) => {
    if (!amount || (amount && amount <= 0) || !recipientId || !country) {
      return Promise.reject(new Error('Invalid parameters'));
    }
    const path = `${PAYEX_URL}/offers`;
    const payload = {
      amount,
      country,
      recipient: recipientId,
      fingerprint,
      locale,
      ...(recurringConfigurationId && {
        recurring_configuration_id: recurringConfigurationId,
      }),
      ...(recipientFields.length ? { recipient_fields: recipientFields } : {}),
      ...(sourceOfFunds && { source_of_funds: sourceOfFunds }),
      ...(financialInstitution && {
        financial_institution: financialInstitution,
      }),
    };
    const options = {
      method: 'POST',
      body: JSON.stringify(payload),
    };

    return fetchWithAuthentication(path, options);
  },
  getSingleOffers: ({ recipientId, country, amount, fingerprint, locale }) => {
    if (!amount || (amount && amount <= 0) || !recipientId || !country) {
      return Promise.reject(new Error('Invalid parameters'));
    }
    const path = `${PAYEX_URL}/offers/single`;

    const payload = {
      amount,
      country,
      recipient: recipientId,
      fingerprint,
      locale,
    };
    const options = {
      method: 'POST',
      body: JSON.stringify(payload),
    };

    return fetchWithAuthentication(path, options);
  },
  getRecurringOffers: ({
    amount,
    country,
    installments,
    recipientId,
    recurringConfigurationId,
    recipientFields,
    fingerprint,
    locale,
    sourceOfFunds,
    financialInstitution,
  }) => {
    if (!amount || (amount && amount <= 0) || !recipientId || !country) {
      return Promise.reject(new Error('Invalid parameters'));
    }
    const path = `${PAYEX_URL}/offers/recurring`;

    const payload = {
      amount,
      country,
      recipient: recipientId,
      fingerprint,
      locale,
      installments,
      ...(recurringConfigurationId && {
        recurring_configuration_id: recurringConfigurationId,
      }),
      ...(recipientFields.length ? { recipient_fields: recipientFields } : {}),
      ...(sourceOfFunds && { source_of_funds: sourceOfFunds }),
      ...(financialInstitution && {
        financial_institution: financialInstitution,
      }),
    };
    const options = {
      method: 'POST',
      body: JSON.stringify(payload),
    };

    return fetchWithAuthentication(path, options);
  },
  getNonFxOptions: () => {
    const path = `${PAYEX_URL}/offers/non_fx_options`;
    const options = {
      method: 'GET',
    };
    return fetchWithAuthentication(path, options);
  },
};

export const normalizeOffers = (
  offers: OfferWithIds[],
): {
  entities: {
    currencies: Record<string, Currency>;
    fields: Record<string, Field>;
    offers: Record<string, OfferWithIds>;
    prices: Record<string, Price>;
    sections: Record<string, Section>;
  };
  result: string[];
} => normalizeResponse(offers, schemas.arrayOfOffers);
